import React, {
  FC,
  useRef,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
} from "react";
import Modal from "../Modal";
import LoadingIcon from "../LodingIcon";
import { getMerchantVoucher } from "../../services";
import { MerchantVoucherCommand } from "../../services/merchantServices";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import {
  XMarkIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

// Font size of the ticket, in px, before fitting it to the width of the modal
const BASE_FONT_SIZE = 13;
const MIN_FONT_SIZE = 8;

interface MerchantVoucherModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  command?: MerchantVoucherCommand;
}

export const MerchantVoucherModal: FC<MerchantVoucherModalProps> = ({
  openModal,
  setOpenModal,
  title,
  command,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [voucher, setVoucher] = useState("");

  const preRef = useRef<HTMLPreElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  // Removes the leading blank lines and the trailing spaces of every line: the
  // padding of the original ticket only adds empty space and horizontal scroll
  const voucherText = useMemo(
    () =>
      voucher
        .replace(/^(?:[ \t]*\r?\n)+/, "")
        .replace(/\s+$/, "")
        .split("\n")
        .map((line) => line.replace(/\s+$/, ""))
        .join("\n"),
    [voucher]
  );

  // Reads the voucher every time the modal is opened, so the content is never stale
  useEffect(() => {
    if (!openModal || !command) return;

    let cancelled = false;

    const getVoucher = async () => {
      setLoading(true);
      setError("");
      setVoucher("");

      const response = await getMerchantVoucher(command);

      if (cancelled) return;

      if (response.didError || !response.model) {
        setError(
          response.errorMessage || "No se pudo obtener el voucher solicitado."
        );
      } else {
        setVoucher(response.model);
      }

      setLoading(false);
    };

    getVoucher();

    return () => {
      cancelled = true;
    };
  }, [openModal, command]);

  // Shrinks the text until the widest line fits, so the ticket never scrolls
  // horizontally. Measuring the rendered width keeps it font agnostic.
  useLayoutEffect(() => {
    const pre = preRef.current;
    const paper = paperRef.current;

    if (!pre || !paper || !voucherText) return;

    const fitVoucher = () => {
      const available = paper.clientWidth;

      pre.style.fontSize = `${BASE_FONT_SIZE}px`;
      const needed = pre.scrollWidth;

      if (!available || !needed) return;

      const fontSize =
        needed > available
          ? Math.max(
              MIN_FONT_SIZE,
              Math.floor((BASE_FONT_SIZE * available) / needed)
            )
          : BASE_FONT_SIZE;

      pre.style.fontSize = `${fontSize}px`;
      pre.style.lineHeight = `${Math.round(fontSize * 1.45)}px`;
    };

    fitVoucher();

    // The available width changes when the window is resized
    const observer = new ResizeObserver(fitVoucher);
    observer.observe(paper);

    return () => observer.disconnect();
  }, [voucherText]);

  // TODO: enviar el voucher al servicio de impresión
  const handlePrint = () => {};

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="w-full sm:max-w-xl"
    >
      {/* Header */}
      <div className="flex flex-row justify-between items-start mb-4 gap-8">
        <h2 className="text-xl font-bold">{title}</h2>

        <div className="ml-3 flex h-7 items-center">
          <button
            type="button"
            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setOpenModal(false)}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Cerrar</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col items-center justify-center bg-gray-100 rounded-lg p-4 min-h-[16rem]">
        {loading && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <LoadingIcon size="3rem" />
            <p className="text-gray-600 font-medium text-center">
              Leyendo voucher. Por favor espere...
            </p>
          </div>
        )}

        {!loading && !!error && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500" />
            <p className="text-gray-600 font-medium text-center">{error}</p>
          </div>
        )}

        {!loading && !error && (
          // Paper sheet: monospace text keeps the alignment of the original ticket
          <div className="w-full bg-white shadow-md border border-gray-200 px-5 py-6 overflow-y-auto overflow-x-hidden max-h-[26rem]">
            <div ref={paperRef} className="w-full">
              <pre
                ref={preRef}
                className="font-mono text-gray-900 whitespace-pre"
                style={{ fontSize: BASE_FONT_SIZE }}
              >
                {voucherText}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-row gap-4 items-center justify-between mt-6">
        <SecondaryButton onClick={() => setOpenModal(false)} className="px-8">
          Cerrar
        </SecondaryButton>

        <PrimaryButton
          disabled={loading || !!error || !voucherText}
          onClick={handlePrint}
          className="px-8 gap-1"
        >
          <PrinterIcon className="h-5 w-5 flex-none" aria-hidden="true" />
          Imprimir
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default MerchantVoucherModal;
